// La cuenta de cuánto comprar, escrita como se escribe una cuenta.
//
// Pedido de Charly: "que quede clara la ecuación que nos dice cuánto comprar". Hasta acá el
// producto decía el RESULTADO —"hay que comprar 3.654"— y de dónde salía ese número no estaba
// en ningún lado: quien aprueba una orden de $30 millones tenía que confiar.
//
// 📌 La decisión de diseño es que esto NO es una explicación en prosa, es una LIQUIDACIÓN:
//    una columna de números alineados a la derecha, con su operador, y una raya antes del
//    total. Es el formato con el que un comprador ya sabe leer una cuenta, y el único en el
//    que se nota a simple vista si algo no cierra. Por eso van cifras tabulares y por eso
//    cada renglón dice de dónde sale el suyo.
//
// ⚠️ Los números NO se recalculan acá: vienen armados del motor (`forecast_sku.ecuacion`).
//    Si esta pantalla rehiciera la cuenta habría DOS definiciones de cómo se compra, y el día
//    que una cambie la otra queda vieja sin que nada avise — es el criterio que en BET 2 se
//    aplicó a los productos derivados y el que sostiene `sentenciasAprobar` acá.
import { formatNumber } from '@shared/format';
import { cn } from '@/lib/cn';
import type { EcuacionDto, OrigenValorDto } from '@shared/types';

interface Props {
  /** Trae adentro los días de espera y de cobertura con los que se calculó: ver `Ecuacion`. */
  ecuacion: EcuacionDto;
  origenColchon: OrigenValorDto;
  origenReorden: OrigenValorDto;
  /** % de la espera con el que se fijó el colchón, si se fijó. */
  safetyPct?: number | null;
  className?: string;
}

export function EcuacionCompra({
  ecuacion: e,
  origenColchon,
  origenReorden,
  safetyPct,
  className,
}: Props) {
  const fijado = (o: OrigenValorDto) => o === 'fijado';
  return (
    <div className={cn('rounded-xl border border-hairline bg-card-2 p-4', className)}>
      <p className="section-label">Cómo se llega a ese número</p>

      <dl className="mt-3 space-y-0 text-sm">
        {/* ── Cómo se arma el punto de reorden ─────────────────────────────────── */}
        {fijado(origenReorden) ? (
          <Renglon
            titulo="Punto de reorden"
            detalle="fijado a mano para este producto"
            valor={e.puntoReorden}
            destacado
            marca="fijado"
          />
        ) : (
          <>
            <Renglon
              titulo="Se vende durante la espera"
              detalle={`lo que sale en los ${e.diasEspera} días que tarda el proveedor`}
              valor={e.duranteLaEspera}
            />
            <Renglon
              titulo="Colchón de seguridad"
              detalle={
                fijado(origenColchon)
                  ? `el ${safetyPct}% de la espera, fijado a mano`
                  : 'por lo despareja que es la venta'
              }
              valor={e.colchon}
              operador="+"
              marca={fijado(origenColchon) ? 'fijado' : undefined}
            />
            <Renglon titulo="Punto de reorden" detalle="al llegar acá, hay que pedir" valor={e.puntoReorden} total destacado />
          </>
        )}

        {/* ── De ahí al objetivo de reposición ──────────────────────────────────── */}
        <Renglon
          titulo="Se vende durante la cobertura"
          detalle={`los ${e.diasCobertura} días que se quieren tener cubiertos`}
          valor={e.duranteLaCobertura}
          operador="+"
        />
        <Renglon titulo="Hasta acá hay que reponer" detalle="el objetivo del pedido" valor={e.objetivo} total />

        {/* ── Y lo que hay que comprar ──────────────────────────────────────────── */}
        <Renglon titulo="Hay hoy" detalle="stock más lo que viene en camino" valor={e.disponible} operador="−" />
        <Renglon titulo="Faltan" valor={e.faltan} total />
        {e.moq > 1 && e.comprar !== e.faltan && (
          <Renglon
            titulo="Redondeado al mínimo del proveedor"
            detalle={`vende de a ${formatNumber(e.moq)}`}
            valor={e.comprar}
            operador="↑"
          />
        )}
      </dl>

      <div className="mt-3 flex items-baseline justify-between border-t-2 border-ink-strong/20 pt-3">
        <p className="text-sm font-semibold text-ink-strong">Hay que comprar</p>
        <p className="cifra text-2xl font-bold text-ink-strong">
          {formatNumber(e.comprar)} <span className="text-sm font-normal text-ink-muted">uds</span>
        </p>
      </div>
    </div>
  );
}

function Renglon({
  titulo,
  detalle,
  valor,
  operador,
  total,
  destacado,
  marca,
}: {
  titulo: string;
  detalle?: string;
  valor: number;
  /** El signo de la operación. Va pegado al número, como en una liquidación. */
  operador?: string;
  /** Lleva raya arriba: es la suma de lo anterior. */
  total?: boolean;
  destacado?: boolean;
  marca?: 'fijado';
}) {
  return (
    <div
      className={cn(
        'flex items-baseline justify-between gap-4 py-1.5',
        total && 'mt-0.5 border-t border-hairline pt-2',
      )}
    >
      <dt className="min-w-0">
        <span className={cn('text-xs', destacado || total ? 'font-semibold text-ink-strong' : 'text-ink')}>
          {titulo}
        </span>
        {marca === 'fijado' && (
          <span className="ml-1.5 rounded bg-subtle px-1.5 py-0.5 text-2xs text-ink-muted">a mano</span>
        )}
        {detalle && <span className="block text-2xs leading-snug text-ink-faint">{detalle}</span>}
      </dt>
      <dd className="shrink-0 whitespace-nowrap">
        {/* El operador va en su propia caja de ancho fijo para que TODOS los números queden
            alineados en la misma columna: una cuenta con las cifras corridas no se lee. */}
        <span className="inline-block w-4 text-right text-xs text-ink-faint">{operador ?? ''}</span>{' '}
        <span className={cn('cifra text-sm', destacado || total ? 'font-semibold text-ink-strong' : 'text-ink')}>
          {formatNumber(valor)}
        </span>
      </dd>
    </div>
  );
}
